import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import generateCaptcha from "../../components/captcha";
import formatDateTime from "../../components/formatDateTime";
import FileUpload from "../../components/ul/FileUpload";
import NotFound from "../../components/ul/NotFound";
import UploadButton from "../../components/ul/UploadButton";
import DeleteButton from "../../components/ul/DeleteButton";
import EditButton from "../../components/ul/EditButton";
import {
  imageSizeExceededMessage,
  imageType,
  invalidImageMessage,
  MAX_FILE_SIZE_IMAGE,
} from "../../components/imageType";
import { useNavigate } from "react-router-dom";

const CashTask = () => {
  const [loader, setLoader] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [file, setFile] = useState(null);
  const [fileStatus, setFileStatus] = useState({ image: null });
  const [fileProcessing, setFileProcessing] = useState({ image: false });
  const [formInfo, setFormInfo] = useState({
    name: "",
    url: "",
    type: "quiz", // 👈 replaced description with type
    coin: 1,
  });
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterType, setFilterType] = useState("all"); // 👈 filter state
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const navigate = useNavigate();

  // Fetch CashTask data
  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.GetCashTask();
      if (resp?.status) {
        setData(resp.data);
        setFilteredData(resp.data); // initialize filteredData
      } else toast.error(resp?.message);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch Cash Tasks");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 🔹 Filter tasks by type
  useEffect(() => {
    if (filterType === "all") setFilteredData(data);
    else setFilteredData(data.filter((t) => t.type === filterType));
  }, [filterType, data]);

  // Select All
  const handleSelectAll = () => {
    if (!selectAll) setSelectedTasks(filteredData.map((t) => t._id));
    else setSelectedTasks([]);
    setSelectAll(!selectAll);
  };

  const handleSelectTask = (id) => {
    setSelectedTasks((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async (id = null) => {
    const confirmed = generateCaptcha(
      "You are about to delete a task. Solve this math problem to confirm."
    );
    if (!confirmed)
      return toast.error("Captcha answer is incorrect", { autoClose: 2000 });

    const body = { id: id ? [id] : selectedTasks };
    setLoader(true);
    try {
      const resp = await Calling.DeleteCashTask(body);
      if (resp?.status) {
        toast.success(resp.message || "Task deleted successfully");
        setSelectedTasks([]);
        setSelectAll(false);
        fetchData();
      } else toast.error(resp?.message);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete task(s)");
    } finally {
      setLoader(false);
    }
  };

  const resetForm = () => {
    setFormInfo({ name: "", url: "", type: "quiz", coin: 1 });
    setFile(null);
    setFileStatus({ image: null });
    setFileProcessing({ image: false });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔹 Validation
    if (!formInfo.name || formInfo.name.trim().length < 3)
      return toast.error("Name must be at least 3 characters");
    if (!formInfo.url) return toast.error("URL is required");
    if (formInfo.coin < 1 || isNaN(formInfo.coin))
      return toast.error("Coin must be at least 1");
    if (!isEditing && !file) return toast.error("Please upload an image");

    try {
      setLoader(true);

      const formData = new FormData();
      Object.keys(formInfo).forEach((key) => {
        formData.append(key, formInfo[key]);
      });
      if (file) formData.append("image", file);

      // 🧩 Ensure API call sends multipart/form-data properly
      const resp = isEditing
        ? await Calling.UpdateCashTask(formData, editingId)
        : await Calling.InsertCashTask(formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

      if (resp?.status) {
        toast.success(
          resp.message ||
            (isEditing
              ? "Task updated successfully"
              : "Task added successfully")
        );
        resetForm();
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to save task");
      }
    } catch (err) {
      console.error("Task submit error:", err);
      toast.error("Failed to submit task");
    } finally {
      setLoader(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFileProcessing({ image: true });
    if (!imageType.includes(selectedFile.type)) {
      toast.error(invalidImageMessage);
      setFileStatus({ image: "error" });
      setFile(null);
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE_IMAGE) {
      toast.error(imageSizeExceededMessage);
      setFileStatus({ image: "error" });
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setFileProcessing({ image: false });
    setFileStatus({ image: "success" });
  };

  const handleFormChange = (e) =>
    setFormInfo({ ...formInfo, [e.target.name]: e.target.value });

  const handleEdit = (task) => {
    setIsEditing(true);
    setEditingId(task._id);
    setFormInfo({
      name: task.name,
      url: task.url,
      type: task.type,
      coin: task.coin,
    });
    setFileStatus({ image: "success" });
  };

  const handleCancelEdit = () => resetForm();

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="w-full md:ml-[4.4rem] px-3 sm:px-4 lg:px-6 mt-16">
          <div className="bg-white rounded-xl shadow-sm mt-4 overflow-hidden">
            {/* Header */}
            <div className="p-6 bg-main-th border-b border-main-blue">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                {isEditing ? "✏️ Edit Cash Task" : "➕ Add New Cash Task"}
              </h1>
              <p className="text-gray-600 text-sm mt-1 tracking-wide">
                {isEditing
                  ? "Modify task details and save your changes."
                  : "Enter the information below to create a new task."}
              </p>
            </div>

            {/* Form Section */}
            <form className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6 bg-white">
              {[
                {
                  label: "Task Name",
                  name: "name",
                  placeholder: "Enter task name",
                },
                { label: "Coin Reward", name: "coin", type: "number" },
                {
                  label: "URL",
                  name: "url",
                  placeholder: "https://example.com",
                },
              ].map((field) => (
                <div key={field.name}>
                  <label className="font-semibold text-gray-700 mb-2 block">
                    {field.label}
                  </label>
                  <input
                    type={field.type || "text"}
                    name={field.name}
                    value={formInfo[field.name]}
                    onChange={handleFormChange}
                    placeholder={field.placeholder}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 shadow-sm focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    min={field.type === "number" ? 1 : undefined}
                  />
                </div>
              ))}

              {/* Task Type */}
              <div>
                <label className="font-semibold text-gray-700 mb-2 block">
                  Task Type
                </label>
                <select
                  name="type"
                  value={formInfo.type}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 shadow-sm focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                >
                  <option value="quiz">Quiz</option>
                  <option value="game">Game</option>
                </select>
              </div>

              {/* Image Upload */}
              <div>
                <label className="font-semibold text-gray-700 mb-2 block">
                  Task Icon
                </label>
                <FileUpload
                  id="image"
                  name="image"
                  accept="image/png, image/jpeg, image/jpg, image/gif"
                  onChange={handleFileChange}
                />
                {fileStatus.image === "success" && (
                  <span className="text-green-600 text-xs mt-1 block">
                    Uploaded ✓
                  </span>
                )}
                {fileStatus.image === "error" && (
                  <span className="text-red-500 text-xs mt-1 block">
                    Invalid file type
                  </span>
                )}
              </div>
            </form>

            {/* Action Buttons */}
            <div className="px-6 pb-6 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl shadow font-medium transition"
                >
                  {isEditing ? "Save Changes" : "Create Task"}
                </button>

                {isEditing && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2.5 rounded-xl shadow font-medium transition"
                  >
                    Cancel
                  </button>
                )}
              </div>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 bg-gray-50 rounded-xl px-4 py-2 shadow-sm font-medium focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
              >
                <option value="all">All Tasks</option>
                <option value="quiz">Quiz</option>
                <option value="game">Game</option>
              </select>
            </div>

            {/* Delete Selected */}
            {selectedTasks.length > 0 && (
              <div className="px-6 pb-4">
                <button
                  onClick={() => handleDeleteSelected()}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-xl font-medium transition shadow"
                >
                  Delete Selected ({selectedTasks.length})
                </button>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200">
              <table className="min-w-full border-separate border-spacing-y-2 text-left">
                <thead className="sticky top-0 bg-main-th text-gray-800 text-xs uppercase">
                  <tr>
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="p-3">#</th>
                    <th className="p-3 hidden md:table-cell">Image</th>
                    <th className="p-3">Name</th>
                    <th className="p-3 hidden md:table-cell">URL</th>
                    <th className="p-3">Coin</th>
                    <th className="p-3">Type</th>
                    <th className="p-3 hidden md:table-cell">Date</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((task, i) => (
                      <tr
                        key={task._id}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedTasks.includes(task._id)}
                            onChange={() => handleSelectTask(task._id)}
                          />
                        </td>
                        <td className="p-3">{i + 1}</td>
                        <td className="p-3 hidden md:table-cell">
                          <img
                            src={task.image}
                            className="w-12 h-12 object-cover rounded-lg border"
                          />
                        </td>
                        <td className="p-3 font-semibold">{task.name}</td>
                        <td className="p-3 hidden md:table-cell">
                          <a
                            href={task.url}
                            target="_blank"
                            className="text-blue-600 underline"
                          >
                            Visit
                          </a>
                        </td>
                        <td className="p-3 font-medium">{task.coin}</td>
                        <td className="p-3 capitalize">{task.type}</td>
                        <td className="p-3 hidden md:table-cell">
                          {formatDateTime(task.createdAt)}
                        </td>
                        <td className="p-3 flex justify-center gap-2">
                          <EditButton onClick={() => handleEdit(task)} />
                          <DeleteButton
                            onClick={() => handleDeleteSelected(task._id)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="py-8">
                        <NotFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CashTask;
